import Axios, { AxiosRequestConfig } from 'axios';
import {
  requireLocalStorageSet,
  safeLocalStorageGet,
  safeLocalStorageRemove,
  safeLocalStorageSet,
} from '../lib/safe-storage';

// Use an explicit API URL when one is provided. Otherwise stay same-origin so
// local CRA development uses package.json's proxy and backend errors are exposed
// as HTTP responses instead of browser CORS/network failures.
export const API_URL = process.env.REACT_APP_API_URL || '';
const DEFAULT_DEV_API_URL = 'http://localhost:5001';

export const apiUrl = (path: string): string => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return API_URL ? `${API_URL.replace(/\/$/, '')}${normalizedPath}` : normalizedPath;
};

export const browserApiUrl = (path: string): string => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const browserApiBase =
    API_URL || (process.env.NODE_ENV === 'development' ? DEFAULT_DEV_API_URL : '');
  return browserApiBase ? `${browserApiBase.replace(/\/$/, '')}${normalizedPath}` : normalizedPath;
};

export const shouldUseBrowserApiRedirect = (): boolean =>
  Boolean(API_URL) || process.env.NODE_ENV === 'development';

export const AXIOS_INSTANCE = Axios.create({
  baseURL: API_URL,
  timeout: 6000000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag to prevent infinite refresh loops
let isRefreshing = false;

type PublicApiEndpoint =
  | { kind: 'exact'; value: string; methods?: string[] }
  | { kind: 'prefix'; value: string; methods?: string[] }
  | { kind: 'regex'; value: RegExp; methods?: string[] };

// Public API endpoints that should not include auth tokens
const PUBLIC_API_ENDPOINTS: PublicApiEndpoint[] = [
  { kind: 'prefix', value: '/v1/auth/invite/' },
  { kind: 'exact', value: '/v1/auth/login' },
  { kind: 'exact', value: '/v1/auth/refresh' },
  { kind: 'exact', value: '/v1/auth/password-reset/request', methods: ['POST'] },
  { kind: 'exact', value: '/v1/auth/password-reset/confirm', methods: ['POST'] },
  { kind: 'exact', value: '/v1/auth/password-reset/self-serve/confirm', methods: ['POST'] },
  { kind: 'exact', value: '/v1/auth/sso/options' },
  { kind: 'exact', value: '/v1/auth/sso/exchange', methods: ['POST'] },
  {
    kind: 'regex',
    value: /^\/v1\/microsite-generator\/public\/magic-links\/[^/]+\/redeem$/,
  },
  {
    kind: 'regex',
    value: /^\/v1\/microsite-generator\/public\/microsites\/[^/]+$/,
  },
  {
    kind: 'regex',
    value: /^\/v1\/microsite-generator\/public\/microsites\/[^/]+\/access-requests$/,
  },
  {
    kind: 'regex',
    value: /^\/v1\/microsite-generator\/public\/microsites\/\d+\/assets\/.+$/,
  },
  {
    kind: 'regex',
    value: /^\/v1\/microsite-generator\/public\/asset-folders\/magic\/[^/]+$/,
  },
  {
    kind: 'regex',
    value: /^\/v1\/microsite-generator\/public\/asset-folders\/[^/]+$/,
  },
  {
    kind: 'regex',
    value: /^\/v1\/microsite-generator\/public\/asset-folders\/[^/]+\/access-requests$/,
  },
  {
    kind: 'regex',
    value: /^\/v1\/microsite-generator\/public\/asset-folders\/[^/]+\/pages\/[^/]+$/,
  },
  {
    kind: 'regex',
    value: /^\/v1\/microsite-generator\/public\/asset-folders\/[^/]+\/pages\/[^/]+\/assets\/.+$/,
  },
  {
    kind: 'regex',
    value: /^\/v1\/microsite-generator\/public\/share\/magic\/[^/]+$/,
  },
  {
    kind: 'regex',
    value: /^\/v1\/microsite-generator\/public\/share\/.+\/access-requests$/,
  },
  {
    kind: 'regex',
    value: /^\/v1\/microsite-generator\/public\/share\/.+\/assets\/.+$/,
  },
  {
    kind: 'regex',
    value: /^\/v1\/microsite-generator\/public\/share\/.+$/,
  },
  {
    kind: 'regex',
    value: /^\/v1\/client-opportunity-ranker\/public\/share\/[^/]+\/redeem$/,
    methods: ['POST'],
  },
  {
    kind: 'regex',
    value: /^\/v1\/client-opportunity-ranker\/public\/workspaces\/[^/]+$/,
    methods: ['GET'],
  },
  {
    kind: 'regex',
    value: /^\/v1\/client-opportunity-ranker\/public\/workspaces\/[^/]+\/revisions$/,
    methods: ['POST'],
  },
  {
    kind: 'regex',
    value: /^\/v1\/pulse\/public\/[^/]+\/[^/]+$/,
    methods: ['GET'],
  },
  {
    kind: 'regex',
    value: /^\/v1\/pulse\/public\/[^/]+\/[^/]+\/submissions$/,
    methods: ['POST'],
  },
  { kind: 'prefix', value: '/r/' },
  { kind: 'prefix', value: '/watch/' },
];

export const isPublicApiEndpoint = (requestPath: string, method?: string): boolean => {
  const requestMethod = method?.toUpperCase();
  return PUBLIC_API_ENDPOINTS.some((entry) => {
    if (entry.methods && (!requestMethod || !entry.methods.includes(requestMethod))) return false;
    if (entry.kind === 'exact') return requestPath === entry.value;
    if (entry.kind === 'prefix') return requestPath.startsWith(entry.value);
    return entry.value.test(requestPath);
  });
};

// Request interceptor - add auth token and tenant context headers
AXIOS_INSTANCE.interceptors.request.use(
  (config) => {
    if (typeof FormData !== 'undefined' && config.data instanceof FormData && config.headers) {
      const headers = config.headers as any;
      if (typeof headers.delete === 'function') {
        headers.delete('Content-Type');
        headers.delete('content-type');
      } else {
        delete headers['Content-Type'];
        delete headers['content-type'];
      }
    }

    // Skip adding auth token for public endpoints. Use startsWith on the
    // request path (not includes) so short prefixes like "/r/" or "/watch/"
    // don't accidentally match nested paths such as "/api/v1/users/r/123".
    const requestPath = (() => {
      const url = config.url || '';
      if (url.startsWith('http://') || url.startsWith('https://')) {
        try {
          return new URL(url).pathname;
        } catch (_) {
          return url;
        }
      }
      return url;
    })();
    const isPublicEndpoint = isPublicApiEndpoint(requestPath, config.method);

    const token = safeLocalStorageGet('auth_token');
    if (token && !isPublicEndpoint) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add platform admin tenant viewing headers. These are set by the useAuth
    // store when platform admin uses "View As". Skip them on public endpoints
    // (recorder/watch/etc.) so a recruiter previewing those pages while signed
    // in does not tag unauthenticated requests with their cross-tenant context.
    if (!isPublicEndpoint) {
      try {
        const authStorage = safeLocalStorageGet('auth-storage');
        if (authStorage) {
          const authState = JSON.parse(authStorage)?.state;

          // Check if viewing as a specific tenant
          if (authState?.viewingAsTenant?.tenantId) {
            config.headers['X-View-As-Tenant'] = authState.viewingAsTenant.tenantId;
          }

          // Check if cross-tenant access is enabled
          if (authState?.crossTenantAccess) {
            config.headers['X-Cross-Tenant-Access'] = 'true';
          }
        }
      } catch (e) {
        // Ignore parsing errors
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Public paths that should not trigger auth redirects
const PUBLIC_PATHS = [
  '/accept-invite',
  '/login',
  '/forgot-password',
  '/reset-password',
  '/auth/sso/callback',
  '/microsite-preview/public',
  '/microsite-preview/link',
  '/opportunity-ranker/share/',
  '/client-opportunity-ranker/share/',
  '/share/',
  '/r/',
  '/watch/',
];

// Check if current page is a public path that shouldn't redirect on auth errors
export const isPublicPath = () => {
  const pathname = window.location.pathname;
  const isPulseSurvey = !pathname.startsWith('/pulse/workshops/')
    && /^\/pulse\/[^/]+\/[^/]+\/?$/.test(pathname);
  return isPulseSurvey || PUBLIC_PATHS.some(path => pathname.startsWith(path));
};

// Response interceptor - handle 401/403 errors (expired or invalid tokens)
AXIOS_INSTANCE.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const errorData = error.response?.data?.detail;

    // Skip auth error handling for public pages
    if (isPublicPath()) {
      return Promise.reject(error);
    }

    // Handle tenant deactivation (403 with specific error type)
    if (status === 403 && errorData?.error === 'tenant_deactivated') {
      // Store deactivation info for the banner
      safeLocalStorageSet('tenant_deactivated', JSON.stringify({
        tenantName: errorData.tenant_name,
        deactivatedAt: errorData.deactivated_at,
        message: errorData.message
      }));

      // Trigger a custom event that the app can listen for
      window.dispatchEvent(new CustomEvent('tenant-deactivated', { detail: errorData }));

      return Promise.reject(error);
    }

    // Handle 401 (Unauthorized) - try to refresh token
    if (status === 401 && !isRefreshing) {
      // If we're already on the login page, don't do anything
      if (window.location.pathname === '/login') {
        return Promise.reject(error);
      }

      isRefreshing = true;
      try {
        // Try to refresh token
        const refreshToken = safeLocalStorageGet('refresh_token');
        if (refreshToken) {
          const response = await AXIOS_INSTANCE.post('/v1/auth/refresh', {
            refresh_token: refreshToken,
          });

          const { access_token, refresh_token: newRefreshToken } = response.data;
          requireLocalStorageSet('auth_token', access_token);
          requireLocalStorageSet('refresh_token', newRefreshToken);

          // Retry original request
          isRefreshing = false;
          error.config.headers.Authorization = `Bearer ${access_token}`;
          return AXIOS_INSTANCE.request(error.config);
        } else {
          // No refresh token - clear auth and redirect to login
          // Keep isRefreshing true to prevent more redirect attempts
          safeLocalStorageRemove('auth_token');
          safeLocalStorageRemove('refresh_token');
          safeLocalStorageRemove('user');
          window.location.href = '/login?message=Your session has expired. Please log in again.';
          return Promise.reject(error);
        }
      } catch (refreshError) {
        // Refresh failed - logout
        // Keep isRefreshing true to prevent more redirect attempts
        safeLocalStorageRemove('auth_token');
        safeLocalStorageRemove('refresh_token');
        safeLocalStorageRemove('user');
        window.location.href = '/login?message=Your session has expired. Please log in again.';
        return Promise.reject(error);
      }
    }

    // Handle 403 (Forbidden) - token invalid or expired
    // This can happen with SSE endpoints or other auth failures
    if (status === 403 && error.response?.data?.message?.includes?.('Authentication failed')) {
      // If we're already on the login page, don't redirect
      if (window.location.pathname === '/login') {
        return Promise.reject(error);
      }

      // Clear auth and redirect to login
      safeLocalStorageRemove('auth_token');
      safeLocalStorageRemove('refresh_token');
      safeLocalStorageRemove('user');
      window.location.href = '/login?message=Your session has expired. Please log in again.';
    }

    return Promise.reject(error);
  }
);

// Custom instance for Orval
export const customInstance = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig,
): Promise<T> => {
  const promise = AXIOS_INSTANCE.request<T>({
    ...config,
    ...options,
  }).then(({ data }) => data);

  return promise;
};

export default customInstance;

// Helper function to get API client for new endpoints
export const getCompaniesApi = () => AXIOS_INSTANCE;
export const getSettingsApi = () => AXIOS_INSTANCE;
